import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Download, Info, Plus, Search } from 'lucide-react';
import { downloadCsv } from '@/lib/utils/csv';
import { InviteViewerModal } from '@/components/modals/directory-create-modals';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { ViewersTable } from '@/components/settings/viewers-table';
import { ViewerPreviewModal } from '@/components/settings/viewer-preview-modal';
import {
  useClientViewerGrantsQuery,
  useReissueViewerMutation,
  useRevokeViewerMutation,
  type ClientViewerGrant,
} from '@/lib/api/settings';

/**
 * Settings → External client viewers (T-0110 / E01-F06).
 *
 * Required reading:
 *   - `ta-agent/output/tickets/T-0110-firm-settings-users-viewers.md`
 *   - `design_handoff_caliber_v2/prototype/screens-firm.js → firmViewers`
 *   - `design_handoff_caliber_v2/prototype/screens-soft-gaps.js → openViewerPreview`
 *
 * Wires three T-0026 endpoints:
 *   - `GET /api/v1/client-viewers` — list (added in this ticket).
 *   - `POST /api/v1/client-viewers/:id/reissue` — re-issue magic link.
 *   - `POST /api/v1/client-viewers/:id/revoke` — revoke access.
 *
 * The "Invite viewer" affordance is a placeholder (the invite-viewer
 * modal is T-0116's misc-modals scope; this ticket lands the surrounding
 * scaffolding). The "Preview viewer experience" CTA on every row opens
 * the `<ViewerPreviewModal>` — see that component for the RLS approach.
 */

export const Route = createFileRoute('/settings/viewers')({
  component: ViewersScreen,
});

function ViewersScreen() {
  const viewersQuery = useClientViewerGrantsQuery();
  const [inviting, setInviting] = React.useState(false);
  const reissue = useReissueViewerMutation();
  const revoke = useRevokeViewerMutation();
  const [search, setSearch] = React.useState('');
  const [previewing, setPreviewing] = React.useState<ClientViewerGrant | null>(null);

  const filtered = React.useMemo(() => {
    const all = viewersQuery.data?.items ?? [];
    if (search.trim().length === 0) return all;
    const needle = search.trim().toLowerCase();
    return all.filter(
      (v) =>
        v.viewer_name.toLowerCase().includes(needle) ||
        v.viewer_email.toLowerCase().includes(needle) ||
        v.client_name.toLowerCase().includes(needle),
    );
  }, [viewersQuery.data, search]);

  const items = viewersQuery.data?.items ?? [];
  const active = items.filter((v) => v.status === 'active').length;
  const pending = items.filter((v) => v.status === 'invited').length;
  const expired = items.filter((v) => v.status === 'revoked').length;

  // Track the grant id whose mutation is in flight so we can disable the
  // row's action buttons during the request. Either mutation can be live
  // at any moment — the pendingGrantId of whichever is currently active
  // wins.
  const pendingGrantId = reissue.variables ?? revoke.variables ?? null;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">External client viewers</h1>
          <p className="text-meta text-ink-500">
            {active} active · {pending} pending · {expired} revoked
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-500"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder="Search viewers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-56 pl-8 text-body-sm"
              aria-label="Search viewers by name, email, or client"
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-meta"
            disabled={items.length === 0}
            onClick={() =>
              downloadCsv(
                'client-viewers.csv',
                ['viewer_name', 'viewer_email', 'auth_mode', 'status'],
                items.map((v) => [v.viewer_name, v.viewer_email, v.auth_mode, v.status]),
              )
            }
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            Export
          </Button>
          <Button
            type="button"
            onClick={() => setInviting(true)}
            variant="default"
            size="sm"
            className="h-8 gap-1.5 text-meta"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Invite viewer
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-card border border-ai-surface-border bg-ai-surface p-3 text-meta text-ai-ink">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden="true" />
        <p className="leading-[18px]">
          External viewers see <strong>read-only snapshots</strong> within the scoped modules. They
          cannot see drafts, audit trail, or AI proposals. Every viewer session is logged and
          surfaces under <em>Audit &rarr; External access</em>.
        </p>
      </div>

      {(reissue.error || revoke.error) && (
        <ErrorMessage error={reissue.error ?? revoke.error ?? null} />
      )}

      {viewersQuery.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner />
          Loading viewers…
        </div>
      ) : viewersQuery.error ? (
        <ErrorMessage error={viewersQuery.error} />
      ) : (
        <ViewersTable
          viewers={filtered}
          onPreview={(v) => setPreviewing(v)}
          onReissue={(v) => reissue.mutate(v.grant_id)}
          onRevoke={(v) => {
            // Soft confirm — the prototype uses a destructive button color
            // but no explicit confirm; the audit trail makes the action
            // recoverable in principle (the revoke is reversible by
            // issuing a new grant).
            if (window.confirm(`Revoke access for ${v.viewer_name}?`)) {
              revoke.mutate(v.grant_id);
            }
          }}
          pendingGrantId={pendingGrantId}
        />
      )}

      <ViewerPreviewModal
        viewer={previewing}
        open={Boolean(previewing)}
        onOpenChange={(open) => {
          if (!open) setPreviewing(null);
        }}
        onReissue={(v) => reissue.mutate(v.grant_id)}
      />

      <InviteViewerModal open={inviting} onOpenChange={setInviting} />
    </div>
  );
}
